/**
 * Errors that mean "this page was loaded from an older (or newer) deployment than the server now
 * runs". None of them can be fixed by re-rendering — only a full page load picks up the current
 * build's chunks and Server Action IDs:
 *
 * - `ChunkLoadError` (Turbopack/webpack): a JS/CSS chunk failed to load. Turbopack caches the
 *   rejected load for the lifetime of the page, so every later use of that module fails instantly
 *   and `reset()` just re-throws.
 * - `UnrecognizedActionError` (Next): the server doesn't know this Server Action ID — the classic
 *   "submitted the login form from a tab opened before the deploy".
 * - "An unexpected response was received from the server." (Next): a Server Action got a non-RSC
 *   response back (HTML error page, redirect, a pod mid-rollout).
 */
const STALE_BUILD_ERROR_NAMES = new Set(['ChunkLoadError', 'UnrecognizedActionError']);
const STALE_BUILD_MESSAGE = /An unexpected response was received from the server|Failed to find Server Action|Failed to fetch dynamically imported module|Loading (CSS )?chunk .+ failed/;

/** sessionStorage key holding the timestamp of the last automatic reload. */
export const STALE_BUILD_RELOAD_KEY = 'bj_stale_build_reload_at';

/** Don't auto-reload again within this window — protects against reload loops. */
export const STALE_BUILD_RELOAD_COOLDOWN_MS = 30_000;

export const isStaleBuildError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const { name, message } = error as { name?: unknown; message?: unknown };
  if (typeof name === 'string' && STALE_BUILD_ERROR_NAMES.has(name)) return true;
  return typeof message === 'string' && STALE_BUILD_MESSAGE.test(message);
};

/**
 * Reloads the page once to pick up the current build. Returns `true` when a reload was triggered.
 *
 * Returns `false` (and does nothing) when another automatic reload happened within the cooldown —
 * then the error is real, not a stale build, and the caller should show its error UI. Also returns
 * `false` when sessionStorage is unavailable, since without the guard a reload could loop forever.
 */
export const reloadOnceForStaleBuild = (): boolean => {
  try {
    const now = Date.now();
    const last = Number(window.sessionStorage.getItem(STALE_BUILD_RELOAD_KEY));
    if (last && now - last < STALE_BUILD_RELOAD_COOLDOWN_MS) return false;
    window.sessionStorage.setItem(STALE_BUILD_RELOAD_KEY, String(now));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
};
